import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { PartnerService } from '@feature/dashboard/partners/services/partner.service';
import { PartnerListInterface } from '@feature/dashboard/partners/types/partner-list';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { sortDirection } from '@sharedModule/constants/data-grid.constants';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { HasPlatformAccessDirective } from '@sharedModule/directives/has-platform-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ExcelExportService } from '@sharedModule/services/excel-export/excel-export.service';
import { ReportFilterDialogComponent } from '@sharedModule/components/report-filter-dialog/report-filter-dialog.component';
import { getActiveSort } from '@sharedModule/utils/sort.utils';
import { Subject, Observable, debounceTime, takeUntil } from 'rxjs';
import { updateSenderListFilter } from '../../store/sender-list.actions';
import { selectSenderList } from '../../store/sender-list.selector';
import { SenderActionsService } from '../../services/sender-actions.service';
import { SenderListInterface } from '../../types/sender-list';
import {
  defaultSenderListFilter,
  SenderListFilter,
} from '../../types/sender-list-filter';

@Component({
  selector: 'app-sender-list',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    HasAccessDirective,
    ReactiveFormsModule,
    HasPlatformAccessDirective,
  ],
  templateUrl: './sender-list.component.html',
  styleUrl: './sender-list.component.scss',
})
export class SenderListComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.senders;
  filterForm!: FormGroup;
  displayedColumns: string[] = [
    'first_name',
    'email',
    'date_of_birth',
    'source_country',
    'status',
    'nationality',
    'mobile_number',
    'action',
  ];
  senderList?: SenderListInterface[];
  dataSource!: MatTableDataSource<SenderListInterface>;
  activeSort?: Sort;
  fallBackSort = {
    active: 'created_at',
    direction: sortDirection.desc,
  };
  page = 0;
  total = 0;
  limit = 10;
  subject = new Subject<{
    partner_id: number;
    search: string;
  }>();
  isSuperAdmin!: boolean;
  partnerList!: Observable<PartnerListInterface[]>;
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private partnerService: PartnerService,
    private senderActions: SenderActionsService,
    private excelExportService: ExcelExportService,
    private authenticationService: AuthenticationService
  ) {
    this.isSuperAdmin = this.authenticationService.isSuperAdmin();
  }

  ngOnInit(): void {
    this.filterFormInit();
    this.loadData();
    this.dispatchFilter();
    const debounceTimeDuration = 1000;
    this.subject
      .pipe(debounceTime(debounceTimeDuration), takeUntil(this.#destroy$))
      .subscribe(res => {
        const partnerId = this.filterForm.value[this.attrConstant.partner.key];
        this.dispatchFilter({
          count: 10,
          page: 0,
          ...res,
          partner_id: ['', null].includes(partnerId) ? null : [partnerId],
        });
      });
    if (this.isSuperAdmin) this.partnerList = this.partnerService.sharedData$;
  }

  filterFormInit(): void {
    this.filterForm = new FormGroup({
      [this.attrConstant.partner.key]: new FormControl(''),
      [this.attrConstant.search.key]: new FormControl(''),
    });
  }

  loadData(): void {
    this.store
      .select(selectSenderList)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) {
          this.senderList = data.rawData;
          this.dataSource = new MatTableDataSource(data.rawData);
          this.page = data.page;
          this.total = data.totalRecords;
          this.limit = data.count;
        }
      });
  }

  applyFilter(): void {
    this.subject.next(this.filterForm.value);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.dispatchFilter({ ...defaultSenderListFilter });
  }

  reload(): void {
    this.dispatchFilter();
  }

  onPageChange(pageEvent: PageEvent): void {
    this.dispatchFilter({
      count: pageEvent.pageSize,
      page: pageEvent.pageIndex,
    });
  }

  onSortChange(sortEvent: Sort): void {
    this.activeSort = getActiveSort(sortEvent, this.fallBackSort);
    this.dispatchFilter({
      sort_key: this.activeSort.active,
      sort_by: this.activeSort.direction,
    });
  }

  dispatchFilter(senderListFilter?: SenderListFilter): void {
    this.store.dispatch(
      updateSenderListFilter({ filter: senderListFilter ?? {} })
    );
  }

  export(): void {
    this.excelExportService.exportToExcel(this.senderList!, 'senders');
  }

  openReport(): void {
    const partnerId = this.filterForm.value[this.attrConstant.partner.key];
    this.dialog.open(ReportFilterDialogComponent, {
      width: '640px',
      data: {
        type: 'sender',
        partnerId: ['', null].includes(partnerId) ? null : partnerId,
      },
    });
  }

  onToggleActive(element: SenderListInterface): void {
    this.handleRowAction(
      this.senderActions.toggleActiveStatus(
        element.sender_id,
        element.is_active
      )
    );
  }

  onUpdateKyc(element: SenderListInterface): void {
    this.handleRowAction(this.senderActions.updateKycStatus(element.sender_id));
  }

  onUpdateAml(element: SenderListInterface): void {
    this.handleRowAction(this.senderActions.updateAmlStatus(element.sender_id));
  }

  private handleRowAction(action$: Observable<boolean>): void {
    action$.pipe(takeUntil(this.#destroy$)).subscribe(success => {
      if (success) this.reload();
    });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
